/**
 * Shared SEO Helper Module
 * 
 * Centralizes SEO meta tag and JSON-LD generation logic for both server and client.
 * Used by server middleware for initial HTML and client useSEO hook for hydration.
 */

const BASE_URL = 'https://www.talenture.ai';
const MAX_META_DESCRIPTION_LENGTH = 160;

function truncateMetaDescription(description: string): string {
  if (description.length <= MAX_META_DESCRIPTION_LENGTH) {
    return description;
  }

  const truncationPoint = description.lastIndexOf(' ', MAX_META_DESCRIPTION_LENGTH - 3);
  return `${description.slice(0, truncationPoint > 0 ? truncationPoint : MAX_META_DESCRIPTION_LENGTH - 3)}...`;
}

export interface SEOData {
  title?: string;
  description?: string;
  keywords?: string[];
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  canonical?: string;
  structuredData?: object;
}

// Default SEO values
export const DEFAULT_TITLE = 'AI Recruiting Software That Works With Your ATS | Talenture.ai';
export const DEFAULT_DESCRIPTION = 'Talenture plugs into your ATS and runs every step from job post to booked interview. Every applicant scored, deal-breakers confirmed, interviews booked. You decide.';
export const DEFAULT_KEYWORDS = ['jobs', 'careers', 'AI recruitment', 'job matching', 'talent acquisition'];

// Job interface for SEO generation - accepts any job object with these fields
export interface JobForSEO {
  id?: string;
  slug?: string;
  title: string;
  description?: string | null;
  companyProfile?: { companyName?: string | null } | null;
  companyWebsite?: string | null;
  companyName?: string | null;
  location?: string | null;
  locationCity?: string | null;
  locationState?: string | null;
  locationCountry?: string | null;
  salaryMin?: number | string | null;
  salaryMax?: number | string | null;
  salaryCurrency?: string | null;
  hardSkills?: unknown;
  softSkills?: unknown;
  category?: string | null;
  employmentType?: string | null;
  workType?: string | null;
  createdAt?: string | Date | null;
  applicationDeadline?: string | Date | null;
}

/**
 * Generate SEO meta tags and structured data for a job posting
 */
export function generateJobSEO(job: JobForSEO, baseUrl?: string): SEOData {
  const hardSkills = Array.isArray(job.hardSkills) ? job.hardSkills as string[] : [];
  const softSkills = Array.isArray(job.softSkills) ? job.softSkills as string[] : [];
  
  const parsedSalaryMin = typeof job.salaryMin === 'string' ? parseFloat(job.salaryMin) : job.salaryMin;
  const parsedSalaryMax = typeof job.salaryMax === 'string' ? parseFloat(job.salaryMax) : job.salaryMax;
  
  const salaryMin = parsedSalaryMin && !isNaN(parsedSalaryMin) ? parsedSalaryMin : null;
  const salaryMax = parsedSalaryMax && !isNaN(parsedSalaryMax) ? parsedSalaryMax : null;
  
  const company = job.companyProfile?.companyName || job.companyName || 'Company';
  const location = job.location ? ` in ${job.location}` : '';
  const salary = salaryMin && salaryMax 
    ? ` | ${job.salaryCurrency || '$'}${Math.round(salaryMin/1000)}k-${Math.round(salaryMax/1000)}k`
    : '';

  const title = `${job.title} - ${company} | Talenture`;
  const description = `Join ${company} as ${job.title}${location}. ${hardSkills.slice(0, 3).join(', ') || 'Great opportunity'} experience preferred${salary}.`;
  
  const keywords = [
    job.title.toLowerCase(),
    ...hardSkills.slice(0, 5).map((skill: string) => skill.toLowerCase()),
    ...softSkills.slice(0, 2).map((skill: string) => skill.toLowerCase()),
    job.category?.toLowerCase() || 'job',
    job.locationCity?.toLowerCase() || '',
    job.employmentType || 'full-time',
    job.workType || 'remote',
    'career',
    'opportunity'
  ].filter(Boolean);

  // Build canonical URL
  const jobUrl = job.slug 
    ? `${baseUrl || ''}/jobs/${job.slug}`
    : job.id 
    ? `${baseUrl || ''}/jobs/${job.id}`
    : baseUrl || '';

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    "title": job.title,
    "description": job.description || description,
    "datePosted": job.createdAt,
    "validThrough": job.applicationDeadline || new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(),
    "employmentType": job.employmentType?.toUpperCase() || "FULL_TIME",
    "hiringOrganization": {
      "@type": "Organization",
      "name": company,
      ...(job.companyWebsite ? { "url": job.companyWebsite } : {}),
    },
    "jobLocationType": !job.location || job.workType === 'remote' ? "TELECOMMUTE" : undefined,
    "jobLocation": job.location ? {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": job.locationCity,
        "addressRegion": job.locationState,
        "addressCountry": job.locationCountry
      }
    } : {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressCountry": job.locationCountry || "NL"
      }
    },
    "baseSalary": salaryMin && salaryMax ? {
      "@type": "MonetaryAmount",
      "currency": job.salaryCurrency || "USD",
      "value": {
        "@type": "QuantitativeValue",
        "minValue": salaryMin,
        "maxValue": salaryMax,
        "unitText": "YEAR"
      }
    } : undefined,
    "skills": hardSkills,
    "workHours": job.employmentType === "part-time" ? "Part-time" : "Full-time",
    "url": jobUrl
  };

  return {
    title,
    description,
    keywords: Array.from(new Set(keywords)),
    ogTitle: title,
    ogDescription: description,
    ogUrl: jobUrl,
    ogImage: `${BASE_URL}/og-image.png`,
    canonical: jobUrl,
    structuredData
  };
}

/**
 * Generate SEO meta tags for job listing page
 */
export function generateJobListingSEO(filters?: {
  query?: string;
  location?: string;
  category?: string;
  seniority?: string;
  employmentType?: string;
}, baseUrl?: string): SEOData {
  const hasFilters = filters?.query || filters?.location;
  
  const title = hasFilters
    ? `${filters.query ? `${filters.query} Jobs` : 'Jobs'}${filters.location ? ` in ${filters.location}` : ''} | Talenture`
    : 'Find Your Perfect Job - AI-Powered Job Search | Talenture';
    
  const description = hasFilters
    ? truncateMetaDescription(`Discover ${filters.query ? `${filters.query} ` : ''}jobs${filters.location ? ` in ${filters.location}` : ''}. Join thousands of professionals finding their perfect career match with AI-powered job recommendations.`)
    : 'Find your next role with Talenture\'s AI-powered job search. Browse top opportunities, get personalized recommendations, and advance your career.';
    
  const keywords = [
    'jobs',
    'careers',
    'job search',
    'employment',
    'hiring',
    'AI recruitment',
    'job matching',
    ...(filters?.query ? [filters.query.toLowerCase()] : []),
    ...(filters?.location ? [filters.location.toLowerCase()] : []),
    ...(filters?.category ? [filters.category.toLowerCase().replace('_', ' ')] : []),
    'talent acquisition',
    'career opportunities'
  ];

  const ogTitle = hasFilters
    ? `${filters.query ? `${filters.query} Jobs` : 'Jobs'}${filters.location ? ` in ${filters.location}` : ''}`
    : 'Find Your Perfect Job - AI-Powered Job Search';
    
  const ogDescription = hasFilters
    ? `Browse ${filters.query ? `${filters.query} ` : ''}opportunities${filters.location ? ` in ${filters.location}` : ''}. AI-powered matching with top companies.`
    : 'Discover your next career opportunity with AI-powered job recommendations from leading companies.';

  const listingUrl = `${baseUrl || ''}/jobs`;

  return {
    title,
    description,
    keywords,
    ogTitle,
    ogDescription,
    ogUrl: listingUrl,
    ogImage: `${BASE_URL}/og-image.png`,
    canonical: listingUrl,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": listingUrl,
      "name": ogTitle,
      "description": ogDescription,
      "url": listingUrl,
      "isPartOf": {
        "@id": `${BASE_URL}/#website`
      }
    }
  };
}

/**
 * Generate HTML meta tags from SEO data (for server-side injection)
 */
export function generateMetaTags(seo: SEOData): string {
  const tags: string[] = [];

  if (seo.title) {
    tags.push(`<title>${escapeHtml(seo.title)}</title>`);
  }

  if (seo.description) {
    tags.push(`<meta name="description" content="${escapeHtml(seo.description)}">`);
  }

  if (seo.keywords && seo.keywords.length > 0) {
    tags.push(`<meta name="keywords" content="${escapeHtml(seo.keywords.join(', '))}">`);
  }

  if (seo.canonical) {
    tags.push(`<link rel="canonical" href="${escapeHtml(seo.canonical)}">`);
  }

  // Open Graph tags
  if (seo.ogTitle) {
    tags.push(`<meta property="og:title" content="${escapeHtml(seo.ogTitle)}">`);
  }

  if (seo.ogDescription) {
    tags.push(`<meta property="og:description" content="${escapeHtml(seo.ogDescription)}">`);
  }

  if (seo.ogUrl) {
    tags.push(`<meta property="og:url" content="${escapeHtml(seo.ogUrl)}">`);
  }

  if (seo.ogImage) {
    tags.push(`<meta property="og:image" content="${escapeHtml(seo.ogImage)}">`);
  }

  tags.push(`<meta property="og:type" content="website">`);
  tags.push(`<meta property="og:site_name" content="Talenture">`);
  tags.push(`<meta property="og:locale" content="en_US">`);

  // Twitter Card tags
  tags.push(`<meta name="twitter:card" content="summary_large_image">`);
  tags.push(`<meta name="twitter:site" content="@TalentureAI">`);
  if (seo.ogTitle) {
    tags.push(`<meta name="twitter:title" content="${escapeHtml(seo.ogTitle)}">`);
  }
  if (seo.ogDescription) {
    tags.push(`<meta name="twitter:description" content="${escapeHtml(seo.ogDescription)}">`);
  }
  if (seo.ogImage) {
    tags.push(`<meta name="twitter:image" content="${escapeHtml(seo.ogImage)}">`);
  }

  // JSON-LD Structured Data
  if (seo.structuredData) {
    // JSON.stringify alone does not make data safe inside a <script> element:
    // a job description containing </script> would otherwise terminate JSON-LD.
    const jsonLd = JSON.stringify(seo.structuredData)
      .replace(/</g, '\\u003c')
      .replace(/>/g, '\\u003e')
      .replace(/&/g, '\\u0026')
      .replace(/\u2028/g, '\\u2028')
      .replace(/\u2029/g, '\\u2029');
    tags.push(`<script type="application/ld+json">${jsonLd}</script>`);
  }

  return tags.join('\n    ');
}

/**
 * Escape HTML entities to prevent XSS
 */
function escapeHtml(text: string): string {
  const map: { [key: string]: string } = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  };
  return text.replace(/[&<>"']/g, (m) => map[m] || m);
}
